import { Autocomplete, Box, CircularProgress, Stack, styled, TextField, Typography } from "@mui/material";
import axios from "axios";
import { ServerEnv } from "Configs/ServerEnv";
import storeCheckout from "GlobalStores/Checkout/StoreCheckout";
import storeCountry from "GlobalStores/Cores/County/StoreCountry";
import StoreTranslate from "GlobalStores/Cores/Translator/StoreTranslate";
import { T } from "GlobalStores/Cores/Translator/useTranslate";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import httpHelper from "Services/HttpHelper";

const JPNAutocompleteAddress = observer(({
    callbackAfterSelect = () => { },
    lastSearchText = '',
    setLastSearchText = () => { },
}) => {
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState(lastSearchText);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const MINIMUM_CHARACTERS = useMemo(() => {
        return 3;
    }, []);

    // Refs for managing API calls and debouncing
    const abortControllerRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const lastSearchValueRef = useRef('');

    // API call function using the same pattern as ModalSearch
    const searchAddress = useCallback(async (searchValue, signal) => {

        try {
            const country3 = storeCountry.Country3();
            const LIMIT = 20
            const url = `https://${ServerEnv.MemberCalls2()}.unicity.com/unishop-fn-misc/city/searchV2?country_code=${country3}&keyword=${searchValue}&limit=${LIMIT}`;

            const { data, status } = await axios.get(httpHelper.Proxy({ originalUrl: url }), {
                signal: signal
            });

            if (signal.aborted) return;

            if (status === 200) {
                if (data.data && data.data.length > 0) {
                    const optionsWithKey = data.data.map((option) => ({
                        ...option,
                        uniqueKey: option.id // สร้าง unique key
                    }));
                    setOptions(optionsWithKey);
                } else {
                    setOptions([]);
                }
            } else {
                setOptions([]);
            }
        } catch (error) {
            if (error.name === 'CanceledError' || error.name === 'AbortError') {
                return;
            }
            console.error('Address search API error:', error);
            setOptions([]);
        }
    }, []);

    // Debounced search function
    const debouncedSearch = useCallback((searchValue, delay = 500) => {
        // Clear existing timer

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Cancel previous API call
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Skip if same as last search
        if (lastSearchValueRef.current === searchValue) {
            return;
        }

        // Set new timer
        debounceTimerRef.current = setTimeout(async () => {
            if (searchValue && searchValue.trim().length >= MINIMUM_CHARACTERS) {
                lastSearchValueRef.current = searchValue;
                setLoading(true);

                // Create new AbortController for this request
                const abortController = new AbortController();
                abortControllerRef.current = abortController;

                try {
                    await searchAddress(searchValue, abortController.signal);
                } finally {
                    if (!abortController.signal.aborted) {
                        setLoading(false);
                    }
                    setOpen(true);
                }
            } else {
                // setOptions([]);
                setLoading(false);
                setOpen(false); // Close dropdown when no options are found
            }
        }, delay);
    }, [searchAddress, MINIMUM_CHARACTERS]);

    // Handle input change with debouncing
    const handleInputChange = useCallback((event, newInputValue, reason) => {
        setInputValue(newInputValue);

        if (reason === 'reset' || reason === 'clear') {
            return;
        }

        if (
            !newInputValue ||
            !newInputValue.trim() ||
            newInputValue.trim().length < MINIMUM_CHARACTERS
        ) {
            setOpen(false);
            lastSearchValueRef.current = '';
            return;
        }


        debouncedSearch(newInputValue.trim());
        setLastSearchText(newInputValue.trim());
    }, [debouncedSearch, MINIMUM_CHARACTERS, setLastSearchText]);

    // Handle selection change
    const handleChange = useCallback((event, newValue) => {

        setValue(newValue);
        if (newValue) {
            storeCheckout.CheckOutInstance().MapAutoAddressToForm(storeCheckout.GetShippingForm(), newValue)
            storeCheckout.CheckOutInstance().MapAutoAddressToForm(storeCheckout.GetCheckoutForms(), newValue)
            setOpen(false);

            lastSearchValueRef.current = newValue[`search_address_${StoreTranslate.IsEnglish() ? 'roman' : 'native'}`]

            const selectedLabel = newValue[`search_address_${StoreTranslate.IsEnglish() ? 'roman' : 'native'}`];
            lastSearchValueRef.current = selectedLabel;

            callbackAfterSelect();
        }

    }, [callbackAfterSelect]);

    // Cleanup function
    const cleanup = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setLoading(false);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    // Option label function - handles both Roman and Native addresses
    const getOptionLabel = useCallback((option) => {
        if (!option) return '';

        const addressKey = `search_address_${StoreTranslate.IsEnglish() ? 'roman' : 'native'}`;

        const address = option[addressKey] || option.search_address_roman || '';
        return address
    }, []);

    // Check if options are equal (for proper option matching)
    const isOptionEqualToValue = useCallback((option, value) => {
        return option?.id === value?.id;
    }, []);

    // useEffect(() => {
    //     console.log("options:", {
    //         length: options.length,
    //         options,
    //     });
    // }, [options]);

    return (
        <Stack>
            <Autocomplete
                id="address-autocomplete"
                value={value}
                inputValue={inputValue}
                options={options}
                fullWidth={true}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={handleChange}
                onInputChange={handleInputChange}
                freeSolo={true}
                noOptionsText={<T>no_address_found</T>}
                filterOptions={(options) => options}
                includeInputInList={false}
                autoFocus={true}
                open={open}
                onClose={() => {
                    setOpen(false);
                }}
                onBlur={(e) => {
                    setOpen(false);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.defaultMuiPrevented = true;
                    }
                }}
                sx={autoCompleteStyle}
                renderInput={(params) => (
                    <TextFieldStyled
                        {...params}
                        label={null}
                        placeholder=" "
                        variant='outlined'
                        type="text"
                        InputProps={{
                            ...params.InputProps,
                            'aria-autocomplete': 'none',
                            spellCheck: 'false',
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} sx={{
                                        '& .MuiCircularProgress-svg': {
                                            animationDuration: '0.6s'
                                        }
                                    }} /> : null}
                                </>
                            ),
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    return (
                        <Box component="li" {...props} key={option.id}>
                            <Box>
                                <Typography
                                    component="h6"
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        marginBottom: '8px',
                                        color: '#33628C',
                                        fontFamily: 'Poppins, NotoSansThai',
                                    }}

                                >
                                    {option.zip}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: '300',
                                        color: '#93AABF',
                                        fontFamily: 'Poppins, NotoSansThai',
                                    }}
                                >
                                    {option[`search_address_${StoreTranslate.IsEnglish() ? 'roman' : 'native'}`]}
                                </Typography>
                            </Box>
                        </Box>
                    )
                }}
                ListboxProps={{
                    style: {
                        maxHeight: '300px',
                    }
                }}
            />
        </Stack>
    );
})

export default JPNAutocompleteAddress

const autoCompleteStyle = {
    '.MuiOutlinedInput-root': {
        borderRadius: '8px',
        color: 'var(--input-text)',
        fontWeight: 400,
        fontSize: 16,
        height: '48px',
        padding: '0 16px',
        backgroundColor: 'var(--input-bg)',
        // filled state: กล่องทั้งใบเปลี่ยนสีเมื่อ input ข้างในมีค่า
        '&:has(input:not(:placeholder-shown))': {
            backgroundColor: 'var(--input-filled-bg)'
        },
        '.MuiAutocomplete-input': {
            padding: 0
        },
        '&:hover': {
            border: '0px!important'
        },
        '&.Mui-focused': {
            border: 0,
            '.MuiOutlinedInput-notchedOutline': {
                border: '1px solid var(--input-focus)'
            }
        }
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        border: '1px solid var(--input-border)',
        '&:hover': {
            border: '0px!important'
        }
    },
    '& .Mui-focused': {
        border: 0,
        '.MuiOutlinedInput-notchedOutline': {
            border: '1px solid var(--input-focus)'
        }
    },
    ' .MuiInputBase-root': {
        '&:hover': {
            border: '0px!important',
            '.MuiOutlinedInput-notchedOutline': {
                border: '1px solid var(--input-border)'
            }
        }
    }
}

const TextFieldStyled = styled(TextField)`
    '& .MuiInputBase-input': {
        boxSizing: 'border-box',
        position: 'relative',
        border: '1px solid var(--input-border)',
        fontSize: 16,
        width: '100%',
        padding: '0 16px',
        '@media not all and (min-resolution:.001dpcm)': {
            '@supports (-webkit-appearance:none)': {
                padding: '0 16px',
            }
        },
        marginTop: '0px',
        borderRadius: '8px',
        color: 'var(--input-text)',
        fontWeight: 400,
        height: '48px',
        fontFamily: 'Inter, NotoSansThai',
    },
    '.MuiOutlinedInput-root': {
        border: '1px solid var(--input-border)',
        borderRadius: 8,
        height: 48
    },
    '.Mui-focused': {
        borderColor: 'rgb(21, 56, 98)',
        color: 'rgb(21, 56, 98)'
    },
    '.MuiOutlinedInput-notchedOutline': {
        display: 'none'
    }
`